<template>
  <div class="google-reviews-panel">

    <!-- Loading State -->
    <div v-if="loading" class="reviews-loading">
      <div class="loading-spinner"></div>
      <p>Loading reviews...</p>
    </div>

    <!-- Empty State - No Reviews -->
    <div v-else-if="featuredReviews.length === 0" class="no-reviews-state">
      <div class="no-reviews-icon">
        <i class="fas fa-star"></i>
      </div>
      <h3>No Reviews Yet</h3>
      <p>Be the first to share your experience with this course!</p>
      <a
        v-if="isLoggedIn"
        href="https://www.google.com/search?q=bheem+academy"
        target="_blank"
        rel="noopener noreferrer"
        class="write-review-btn-empty"
      >
        <i class="fab fa-google"></i>
        Write a Review on Google
      </a>
      <p v-else class="login-prompt">
        <a href="/login/" class="login-link">Login</a> to write a review
      </p>
    </div>

    <!-- Reviews Content - Only show when there are reviews -->
    <template v-else>
      <!-- Reviews Header with Stats -->
      <div class="reviews-header">
        <div class="reviews-stats">
          <div class="overall-rating">
            <div class="rating-display">
              <div class="rating-number">{{ aggregate.averageRating.toFixed(1) }}</div>
              <div class="rating-details">
                <div class="stars">
                  <i v-for="n in 5" :key="n" class="fas fa-star"></i>
                </div>
                <div class="total-reviews">Based on {{ aggregate.totalReviews }} Google Reviews</div>
              </div>
            </div>
          </div>
          <a
            v-if="isLoggedIn"
            href="https://www.google.com/search?q=bheem+academy#lrd=0x487604c7b0e6f5b5:0x1234567890abcdef,1"
            target="_blank"
            rel="noopener noreferrer"
            class="write-review-btn"
          >
            <i class="fab fa-google"></i>
            Write a Review
          </a>
        </div>
      </div>

      <!-- Featured Reviews Section -->
      <div class="featured-reviews">
        <h3 class="section-title">
          <i class="fas fa-trophy"></i>
          Featured Student Reviews
        </h3>
        <div class="featured-grid">
          <div
            v-for="review in featuredReviews"
            :key="review.id"
            class="featured-review-card"
          >
            <div class="review-header">
              <div class="reviewer-avatar">
                <i :class="review.avatarIcon"></i>
              </div>
              <div class="reviewer-info">
                <h4>{{ review.name }}</h4>
                <div class="review-meta">
                  <div class="stars">
                    <i v-for="n in review.rating" :key="n" class="fas fa-star"></i>
                  </div>
                  <span class="review-date">{{ review.date }}</span>
                </div>
              </div>
              <i class="fab fa-google google-icon"></i>
            </div>
            <p class="review-text">{{ review.text }}</p>
            <div class="review-tags">
              <span
                v-for="tag in review.tags"
                :key="tag"
                class="tag"
              >{{ tag }}</span>
            </div>
            <div class="review-footer">
              <div class="verified-badge">
                <i class="fas fa-check-circle"></i>
                Verified Google Review
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from 'vue';

const props = defineProps({
  courseId: {
    type: Number,
    default: 0
  }
});

// State
const loading = ref(true);
const featuredReviews = ref([]);
const aggregate = ref({
  totalReviews: 0,
  averageRating: 5.0
});

// Check if user is logged in
const isLoggedIn = computed(() => {
  return window.CURRENT_USER && window.CURRENT_USER.isLoggedIn === true;
});

// Fetch reviews from API
async function fetchReviews() {
  try {
    loading.value = true;

    // Build URL with optional course_id filter
    let url = '/api/v1/academy/reviews?featured_only=true&limit=10';
    if (props.courseId > 0) {
      url += `&course_id=${props.courseId}`;
    }

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to fetch reviews');
    }

    const data = await response.json();

    featuredReviews.value = data.reviews || [];
    aggregate.value = data.aggregate || { totalReviews: 0, averageRating: 5.0 };

  } catch (error) {
    console.error('Error fetching reviews:', error);
    // Keep default values on error
  } finally {
    loading.value = false;
  }
}

// Fetch reviews when component mounts
onMounted(() => {
  fetchReviews();
});
</script>

<style scoped>
.google-reviews-panel {
  padding: 0;
}

/* Loading State */
.reviews-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #64748b;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(102, 126, 234, 0.2);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Empty State - No Reviews */
.no-reviews-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  text-align: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 20px;
  border: 2px dashed #cbd5e1;
}

.no-reviews-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 8px 24px rgba(251, 191, 36, 0.3);
}

.no-reviews-icon i {
  font-size: 48px;
  color: white;
}

.no-reviews-state h3 {
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 12px;
}

.no-reviews-state p {
  font-size: 16px;
  color: #64748b;
  margin: 0 0 32px;
  max-width: 400px;
}

.write-review-btn-empty {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-radius: 12px;
  font-weight: 700;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
}

.write-review-btn-empty:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
}

.write-review-btn-empty i {
  font-size: 20px;
}

/* Login Prompt for Empty State */
.login-prompt {
  font-size: 16px;
  color: #64748b;
  margin: 0;
}

.login-prompt .login-link {
  color: #3b82f6;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
}

.login-prompt .login-link:hover {
  text-decoration: underline;
  color: #2563eb;
}

/* Login to Review Button */
.login-to-review {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%) !important;
  color: white !important;
}

.login-to-review:hover {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
}

/* Reviews Header */
.reviews-header {
  background: linear-gradient(135deg, #3b82f6 0%, #1a1a1a 100%);
  color: white;
  padding: 48px;
  border-radius: 20px;
  margin-bottom: 40px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.reviews-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
}

.reviews-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  position: relative;
  z-index: 1;
}

.rating-display {
  display: flex;
  align-items: center;
  gap: 20px;
}

.rating-number {
  font-size: 72px;
  font-weight: 900;
  line-height: 1;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.rating-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stars {
  display: flex;
  gap: 4px;
  font-size: 24px;
  color: #FCD34D;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.total-reviews {
  font-size: 15px;
  opacity: 0.95;
  font-weight: 500;
}

.write-review-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 32px;
  background: white;
  color: #3b82f6;
  border-radius: 12px;
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.write-review-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
  background: #f8f9fa;
}

.write-review-btn i {
  font-size: 18px;
}

/* Section Title */
.section-title {
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-title i {
  color: #3b82f6;
  font-size: 32px;
}

/* Featured Reviews Grid */
.featured-reviews {
  margin-bottom: 56px;
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 24px;
}

.featured-review-card {
  background: white;
  border: 2px solid rgba(102, 126, 234, 0.1);
  border-radius: 16px;
  padding: 28px;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.featured-review-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #1a1a1a);
  transform: scaleX(0);
  transition: transform 0.4s ease;
}

.featured-review-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.25);
  border-color: rgba(102, 126, 234, 0.4);
}

.featured-review-card:hover::before {
  transform: scaleX(1);
}

/* Review Header */
.review-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;
}

.reviewer-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #1a1a1a);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
}

.reviewer-info {
  flex: 1;
}

.reviewer-info h4 {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px;
}

.review-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.review-meta .stars {
  display: flex;
  gap: 2px;
  font-size: 14px;
  color: #FCD34D;
}

.review-date {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.google-icon {
  font-size: 26px;
  color: #4285F4;
  flex-shrink: 0;
}

/* Review Text */
.review-text {
  font-size: 15px;
  line-height: 1.7;
  color: #475569;
  margin-bottom: 16px;
}

/* Review Tags */
.review-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.tag {
  font-size: 11px;
  font-weight: 600;
  color: #3b82f6;
  background: rgba(102, 126, 234, 0.1);
  padding: 5px 12px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Review Footer */
.review-footer {
  padding-top: 16px;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
}

.verified-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
}

.verified-badge i {
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 968px) {
  .reviews-header {
    padding: 32px 24px;
  }

  .rating-display {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .rating-number {
    font-size: 56px;
  }

  .featured-grid {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 24px;
  }
}

@media (max-width: 768px) {
  .reviews-header {
    padding: 24px 20px;
  }

  .reviews-stats {
    flex-direction: column;
    align-items: flex-start;
  }

  .rating-number {
    font-size: 48px;
  }

  .stars {
    font-size: 20px;
  }

  .write-review-btn {
    width: 100%;
    justify-content: center;
  }

  .featured-review-card {
    padding: 20px;
  }
}
</style>
